import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        // Vérifier si un token existe au chargement
        const token = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('user');

        if (token) {
            // Si on a un utilisateur en cache, on l'affiche temporairement
            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                    setIsAuthenticated(true);
                } catch (e) {
                    console.error('Erreur parsing user:', e);
                }
            }

            // TOUJOURS vérifier avec le backend pour des données fraîches
            checkAuth();
        } else {
            setLoading(false);
        }
    }, []);

    const checkAuth = async () => {
        try {
            console.log("🔐 AUTH CHECK - Triggered");
            const response = await authAPI.getMe();
            const freshUser = response.data.user;
            console.log("🔐 AUTH CHECK - Backend User Role:", freshUser.role);

            setUser(freshUser);
            setIsAuthenticated(true);
            localStorage.setItem('user', JSON.stringify(freshUser));
        } catch (error) {
            console.error('Auth check failed:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            const response = await authAPI.login(email, password);
            const { user, token } = response.data;

            localStorage.setItem('auth_token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.removeItem('isLocked');

            setUser(user);
            setIsAuthenticated(true);

            toast.success(`Bienvenue ${user.name} !`);
            return { success: true, user };
        } catch (error) {
            const message = error.response?.data?.message || 'Erreur de connexion';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const register = async (data) => {
        try {
            const response = await authAPI.register(data);
            const { user, token } = response.data;

            localStorage.setItem('auth_token', token);
            localStorage.setItem('user', JSON.stringify(user));

            setUser(user);
            setIsAuthenticated(true);

            toast.success('Inscription réussie !');
            return { success: true, user };
        } catch (error) {
            const message = error.response?.data?.message || 'Erreur lors de l\'inscription';
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
            toast.info('Vous avez été déconnecté');
        }
    };

    const updateUser = (updatedUser) => {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
    };

    // Vérifications de rôle
    const isAdmin = () => user?.role === 'admin';
    const isManager = () => user?.role === 'manager';
    const isCollaborateur = () => user?.role === 'collaborateur';

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        register,
        logout,
        updateUser,
        refreshAuth: checkAuth,
        isAdmin,
        isManager,
        isCollaborateur,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
